import { useEffect, useRef, useState } from 'react';
import {
  ArrowUpTrayIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

interface ComplianceDoc {
  item_id: string;
  title: string;
  original_name: string;
  source_agency: string;
  jurisdiction: string;
  doc_type: string;
  has_extracted_text: boolean;
  created_at: string;
  size_bytes: number;
}

const DOC_TYPES = [
  { value: 'guideline', label: 'Guideline' },
  { value: 'regulation', label: 'Regulation' },
  { value: 'advisory', label: 'Advisory' },
  { value: 'certification', label: 'Certification' },
  { value: 'other', label: 'Other' },
];

const DOC_TYPE_BADGES: Record<string, string> = {
  guideline: 'bg-teal-50 text-teal-600 border-teal-100',
  regulation: 'bg-copper-50 text-copper-600 border-copper-100',
  advisory: 'bg-sage-50 text-sage-600 border-sage-100',
  certification: 'bg-cream-100 text-charcoal-500 border-charcoal-100',
  other: 'bg-charcoal-50 text-charcoal-400 border-charcoal-100',
};

const ALLOWED_EXTENSIONS = ['.pdf', '.txt', '.md', '.doc', '.docx'];
const MAX_SIZE = 15 * 1024 * 1024;

function formatSize(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
}

export default function ComplianceDocsCard() {
  const [documents, setDocuments] = useState<ComplianceDoc[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sourceAgency, setSourceAgency] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [docType, setDocType] = useState('guideline');
  const [notes, setNotes] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/onboarding/compliance-docs', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setDocuments(data.documents || []);
    } catch {
      // Listing failure is non-fatal; upload form still usable
    }
  };

  const validateFile = (file: File): string | null => {
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `${file.name}: unsupported format. Use PDF, TXT, MD, DOC, or DOCX.`;
    }
    if (file.size > MAX_SIZE) {
      return `${file.name}: too large. Max 15MB.`;
    }
    return null;
  };

  const handleFileSelected = (file: File) => {
    setError('');
    const problem = validateFile(file);
    if (problem) {
      setError(problem);
      return;
    }
    setSelectedFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) handleFileSelected(e.dataTransfer.files[0]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) handleFileSelected(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Choose a file to upload first.');
      return;
    }
    if (!sourceAgency.trim() || !jurisdiction.trim()) {
      setError('Source agency and jurisdiction are required.');
      return;
    }

    setError('');
    setIsUploading(true);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('source_agency', sourceAgency.trim());
    formData.append('jurisdiction', jurisdiction.trim());
    formData.append('doc_type', docType);
    formData.append('notes', notes.trim());

    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/onboarding/upload/compliance-doc', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.status === 413) {
        setError(`${selectedFile.name}: too large. Max 15MB.`);
        return;
      }
      if (res.status === 415) {
        setError(`${selectedFile.name}: unsupported format. Use PDF, TXT, MD, DOC, or DOCX.`);
        return;
      }

      const data = await res.json();
      if (res.ok && data.success) {
        setSelectedFile(null);
        setSourceAgency('');
        setJurisdiction('');
        setDocType('guideline');
        setNotes('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        await loadDocuments();
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-charcoal-100 shadow-sm overflow-hidden">
      <div className="p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="p-2 bg-sage-50 rounded-xl">
            <ShieldCheckIcon className="h-5 w-5 text-sage-500" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-charcoal-500">Compliance documentation</h2>
            <p className="text-sm text-charcoal-300 mt-0.5">
              Upload advertising standards or bulletins from your regulatory body and all generated
              content will follow them.
            </p>
          </div>
        </div>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-all
            ${dragOver
              ? 'border-copper-400 bg-copper-50'
              : 'border-charcoal-200 hover:border-copper-300 hover:bg-cream-50'
            }
            ${isUploading ? 'opacity-60 pointer-events-none' : ''}
          `}
        >
          <div className="flex flex-col items-center">
            <ArrowUpTrayIcon className="h-8 w-8 text-copper-400 mb-2" />
            <p className="text-sm font-medium text-charcoal-600">
              {selectedFile
                ? selectedFile.name
                : dragOver
                  ? 'Drop your document here'
                  : 'Upload a compliance document'}
            </p>
            <p className="text-xs text-charcoal-400 mt-1">PDF, TXT, MD, DOC, or DOCX — up to 15MB</p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.txt,.md,.doc,.docx"
          onChange={handleInputChange}
          className="hidden"
        />

        {/* Metadata fields */}
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="compliance_source_agency" className="block text-sm font-medium text-charcoal-500 mb-1">
                Source agency <span className="text-copper-500">*</span>
              </label>
              <input
                id="compliance_source_agency"
                type="text"
                value={sourceAgency}
                onChange={(e) => setSourceAgency(e.target.value)}
                className="input"
                placeholder="e.g. College of Chiropractors of Ontario"
              />
            </div>
            <div>
              <label htmlFor="compliance_jurisdiction" className="block text-sm font-medium text-charcoal-500 mb-1">
                Jurisdiction <span className="text-copper-500">*</span>
              </label>
              <input
                id="compliance_jurisdiction"
                type="text"
                value={jurisdiction}
                onChange={(e) => setJurisdiction(e.target.value)}
                className="input"
                placeholder="e.g. Ontario, Canada"
              />
            </div>
          </div>

          <div>
            <label htmlFor="compliance_doc_type" className="block text-sm font-medium text-charcoal-500 mb-1">
              Document type <span className="text-copper-500">*</span>
            </label>
            <select
              id="compliance_doc_type"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="input"
            >
              {DOC_TYPES.map(dt => (
                <option key={dt.value} value={dt.value}>{dt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="compliance_notes_field" className="block text-sm font-medium text-charcoal-500 mb-1">
              Notes
            </label>
            <textarea
              id="compliance_notes_field"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="input"
              placeholder="Anything specific we should know about this document (optional)"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <button
            type="button"
            onClick={handleUpload}
            disabled={isUploading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-copper-500 text-white text-sm font-semibold rounded-xl hover:bg-copper-600 transition-colors disabled:opacity-50 shadow-sm"
          >
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Uploading...
              </>
            ) : (
              <>
                <ArrowUpTrayIcon className="h-4 w-4" />
                Upload Document
              </>
            )}
          </button>
        </div>

        {/* Document list */}
        {documents.length > 0 && (
          <div className="mt-6 pt-5 border-t border-charcoal-100 space-y-3">
            <h3 className="text-sm font-semibold text-charcoal-500">Your documents</h3>
            {documents.map(doc => (
              <div
                key={doc.item_id}
                className="flex items-start gap-3 p-3 rounded-xl border border-charcoal-100 bg-cream-50"
              >
                <div className="p-2 bg-white rounded-lg border border-charcoal-100 flex-shrink-0">
                  <DocumentTextIcon className="h-5 w-5 text-charcoal-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-charcoal-600 truncate">
                    {doc.title || doc.original_name}
                  </p>
                  <p className="text-xs text-charcoal-400 mt-0.5 truncate">
                    {doc.source_agency}
                    {doc.jurisdiction ? ` — ${doc.jurisdiction}` : ''}
                    {doc.size_bytes ? ` · ${formatSize(doc.size_bytes)}` : ''}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${DOC_TYPE_BADGES[doc.doc_type] || DOC_TYPE_BADGES.other}`}
                    >
                      {doc.doc_type}
                    </span>
                    {doc.has_extracted_text && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-sage-100 text-sage-600 border border-sage-200">
                        <ShieldCheckIcon className="h-3 w-3" />
                        feeds your content rules
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
